// Issue #366: Beta star-rating results page (under "Beta" menu).
// Displays the number of testers who submitted a rating and the average value.
import React, { useState, useEffect } from 'react';
import { betaApi } from '../../services/api';

function BetaRatings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchRatings = async () => {
      try {
        const response = await betaApi.listRatings();
        if (!cancelled) {
          setData(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Beta ratings error:', err);
          setError('Fehler beim Laden der Sternebewertungen');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchRatings();
    return () => { cancelled = true; };
  }, []);

  const formatAverage = (avg) =>
    avg === null || avg === undefined ? '–' : avg.toFixed(2);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading" role="status" aria-live="polite">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Lade Sternebewertungen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>Sternebewertung</h1>
            <p className="page-subtitle">Ergebnisse aus dem „Beta endet morgen"-E-Mail</p>
          </div>
        </div>
        <div className="dashboard-error" role="alert">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const count = data?.count ?? 0;
  const average = data?.average ?? null;
  const ratings = data?.ratings ?? [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Sternebewertung</h1>
          <p className="page-subtitle">
            Ergebnisse aus dem „Beta endet morgen"-E-Mail (Issue #366)
          </p>
        </div>
      </div>

      <section aria-label="Zusammenfassung der Sternebewertungen">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" aria-hidden="true">👥</div>
            <div className="stat-content">
              <h3 className="stat-title">Anzahl Bewertungen</h3>
              <div className="stat-value" aria-label={`${count} Personen haben eine Sternebewertung abgegeben`}>
                {count.toLocaleString('de-DE')}
              </div>
              <p className="stat-subtitle">Personen, die eine Bewertung abgegeben haben</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" aria-hidden="true">⭐</div>
            <div className="stat-content">
              <h3 className="stat-title">Durchschnittliche Bewertung</h3>
              <div
                className="stat-value"
                aria-label={
                  average === null
                    ? 'Noch keine Durchschnittsbewertung verfügbar'
                    : `Durchschnittliche Bewertung: ${formatAverage(average)} von 5 Sternen`
                }
              >
                {formatAverage(average)}{average !== null && <span aria-hidden="true"> / 5</span>}
              </div>
              <p className="stat-subtitle">Skala 1–5</p>
            </div>
          </div>
        </div>
      </section>

      {ratings.length > 0 && (
        <section aria-labelledby="beta-rating-list-heading" style={{ marginTop: '32px' }}>
          <h2 id="beta-rating-list-heading" style={{ fontSize: '18px', marginBottom: '12px' }}>
            Einzelbewertungen
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table
              className="data-table"
              aria-label="Liste aller abgegebenen Sternebewertungen mit Sprache und Zeitpunkt"
            >
              <thead>
                <tr>
                  <th scope="col">Bewertung</th>
                  <th scope="col">Sprache</th>
                  <th scope="col">Abgegeben am</th>
                </tr>
              </thead>
              <tbody>
                {ratings.map((row) => (
                  <tr key={row.user_id}>
                    <td aria-label={`${row.rating} von 5 Sternen`}>
                      {row.rating} / 5
                    </td>
                    <td>{row.language === 'en' ? 'Englisch' : 'Deutsch'}</td>
                    <td>{formatDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default BetaRatings;
